import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, Alert, Box, Button, CircularProgress, Grid, Paper, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import type { Options } from 'highcharts';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';
import ChartCard from '@components/charts/ChartCard';
import { seriesPalette, tokenFor } from '@utils/chartTokens';
import {
  reportingService,
  type OrganizationDashboard,
  type Severity,
} from '@services/reportingService';
import { useActiveOrgStore } from '@store/index';
import { formatNumber } from '@utils/format';
import { stripCodeSpans } from '@utils/codeSpans';

const SEVERITY_ORDER: Severity[] = ['critical', 'major', 'minor', 'info'];
const WCAG_ORDER: Array<keyof OrganizationDashboard['by_wcag_level']> = [
  'A',
  'AA',
  'AAA',
  'unspecified',
];

// REV-815: colours come from the shared token table, per theme. They used to
// be pinned here for light mode only, and this page was where the damage
// showed: in dark mode #4527A0 rendered at 1.63:1 on the Paper and #7B1FA2 at
// 2.03:1, against the 3:1 a graphical object needs. The e2e spec now measures
// the rendered fills, so this cannot regress quietly.
//
// Chart colour is never the sole information channel here — every chart ships
// a data-table equivalent below it (WCAG dogfood: charts pass 1.4.1 via the
// table).

const CHART_HEIGHT = 300;

/**
 * REV-483: the engine's priority scale, most urgent first. 5 is the most
 * urgent (unnamed dialogs, keyboard traps); 1 the least (a title attribute
 * duplicating an accessible name). Descending because the chart is read
 * top-down for "what do I fix next".
 */
const PRIORITY_ORDER = ['5', '4', '3', '2', '1'] as const;

/**
 * REV-457: homepage dashboard. Presents an org-level accessibility snapshot as
 * Highcharts charts (per the project charting standard) — findings by severity,
 * by WCAG level, by tracked place, and the top offending rules — each paired
 * with an accessible data table. The homepage is unscoped, so the organization
 * is chosen from a picker (defaulting to the active/first org), mirroring the
 * global Findings surface (REV-456).
 */
function HomePage() {
  const { t } = useTranslation();
  const { activeOrg } = useActiveOrgStore();
  const mode = useTheme().palette.mode;

  /**
   * The current org list, for the 404 fallback below.
   *
   * A REF, not a dependency. `load` is invoked from an effect keyed on its own
   * identity, so listing `orgs` in its deps meant that when the organization
   * list resolved, load() was recreated and RE-RAN: a second fetch, `loading`
   * flipped back to true, and the whole dashboard — Highcharts cards included —
   * unmounted and redrew on every page load.
   */
  // REV-589: the header org switcher is the single mechanism for choosing an
  // organization. The page used to carry its own picker (and, briefly, an
  // "All organizations" rollup — REV-568), which meant two competing controls
  // for the same decision. `activeOrg` already carries id AND slug, so the
  // slug->id round trip this page used to make is gone too.
  const orgId = activeOrg?.id ?? null;
  const orgSlug = activeOrg?.slug ?? '';
  const [data, setData] = useState<OrganizationDashboard | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  /** A tracked place's label. One organization at a time, so no qualifier. */
  const contextLabel = useCallback((r: { name: string | null }) => r.name || '—', []);

  const load = useCallback(async () => {
    if (!orgId) {
      // No organization chosen in the header yet — nothing to fetch, and the
      // empty state below explains where to choose one.
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      setData(await reportingService.getOrganizationDashboard(orgId));
    } catch {
      setError(t('pages.home.error', "We couldn't load your dashboard."));
    } finally {
      setLoading(false);
    }
  }, [orgId, t]);

  useEffect(() => {
    void load();
  }, [load]);

  const severityLabel = useCallback((s: Severity) => t(`pages.home.severities.${s}`, s), [t]);

  const severityOptions: Options | null = useMemo(() => {
    if (!data) return null;
    return {
      chart: { type: 'column', height: CHART_HEIGHT },
      title: { text: undefined },
      xAxis: { categories: SEVERITY_ORDER.map(severityLabel), title: { text: null } },
      yAxis: {
        min: 0,
        allowDecimals: false,
        title: { text: t('pages.home.charts.count', 'Open findings') },
      },
      legend: { enabled: false },
      series: [
        {
          type: 'column',
          name: t('pages.home.charts.count', 'Open findings'),
          colorByPoint: true,
          colors: SEVERITY_ORDER.map((s) => tokenFor('severity', s, mode).fill),
          data: SEVERITY_ORDER.map((s) => data.by_severity?.[s] ?? 0),
        },
      ],
    };
  }, [data, severityLabel, mode, t]);

  const wcagOptions: Options | null = useMemo(() => {
    if (!data) return null;
    return {
      chart: { type: 'column', height: CHART_HEIGHT },
      title: { text: undefined },
      xAxis: {
        categories: WCAG_ORDER.map((l) => (l === 'unspecified' ? '—' : l)),
        title: { text: null },
      },
      yAxis: {
        min: 0,
        allowDecimals: false,
        title: { text: t('pages.home.charts.count', 'Open findings') },
      },
      legend: { enabled: false },
      series: [
        {
          type: 'column',
          name: t('pages.home.charts.count', 'Open findings'),
          color: seriesPalette(mode)[0],
          data: WCAG_ORDER.map((l) => data.by_wcag_level[l] ?? 0),
        },
      ],
    };
  }, [data, mode, t]);

  // REV-483. Both guards tolerate a payload without the new keys: the API
  // deploys before the UI, so for a window the dashboard response is the old
  // shape and these cards simply do not render.
  const contentCategoryRows = useMemo(() => data?.by_content_category ?? [], [data]);

  const contentCategoryOptions: Options | null = useMemo(() => {
    if (contentCategoryRows.length === 0) return null;
    return {
      // Horizontal bars: category names are long ('Images and other non-text
      // content'), and a column chart would either clip or rotate them.
      chart: {
        type: 'bar',
        height: Math.max(CHART_HEIGHT, contentCategoryRows.length * 32 + 80),
      },
      title: { text: undefined },
      xAxis: { categories: contentCategoryRows.map((r) => r.category), title: { text: null } },
      yAxis: {
        min: 0,
        allowDecimals: false,
        title: { text: t('pages.home.charts.count', 'Open findings') },
      },
      legend: { enabled: false },
      series: [
        {
          type: 'bar',
          name: t('pages.home.charts.count', 'Open findings'),
          color: seriesPalette(mode)[4],
          data: contentCategoryRows.map((r) => r.count),
        },
      ],
    };
  }, [contentCategoryRows, mode, t]);

  /** Priority rows, most urgent first, with `unknown` appended only if non-zero. */
  const priorityRows = useMemo(() => {
    if (!data?.by_priority) return [];
    const scale = PRIORITY_ORDER.map((level) => ({
      key: level,
      label:
        level === '5'
          ? t('pages.home.charts.priorityHighest', '5 — highest')
          : level === '1'
            ? t('pages.home.charts.priorityLowest', '1 — lowest')
            : level,
      count: data.by_priority[level] ?? 0,
    }));
    const unknown = data.by_priority.unknown ?? 0;
    // Only shown when it has a value. An always-present "Not rated: 0" row is
    // noise; when it IS non-zero it is the reason the parts add up.
    return unknown > 0
      ? [
          ...scale,
          {
            key: 'unknown',
            label: t('pages.home.charts.priorityUnknown', 'Not rated'),
            count: unknown,
          },
        ]
      : scale;
  }, [data, t]);

  const priorityOptions: Options | null = useMemo(() => {
    if (priorityRows.length === 0) return null;
    return {
      chart: { type: 'column', height: CHART_HEIGHT },
      title: { text: undefined },
      xAxis: { categories: priorityRows.map((r) => r.label), title: { text: null } },
      yAxis: {
        min: 0,
        allowDecimals: false,
        title: { text: t('pages.home.charts.count', 'Open findings') },
      },
      legend: { enabled: false },
      series: [
        {
          type: 'column',
          name: t('pages.home.charts.count', 'Open findings'),
          color: seriesPalette(mode)[3],
          data: priorityRows.map((r) => r.count),
        },
      ],
    };
  }, [priorityRows, mode, t]);

  const byContextOptions: Options | null = useMemo(() => {
    if (!data || data.by_context.length === 0) return null;
    const rows = data.by_context;
    return {
      chart: { type: 'bar', height: Math.max(CHART_HEIGHT, rows.length * 44 + 80) },
      title: { text: undefined },
      xAxis: { categories: rows.map((r) => r.name || '—'), title: { text: null } },
      yAxis: {
        min: 0,
        allowDecimals: false,
        title: { text: t('pages.home.charts.count', 'Open findings') },
      },
      legend: { enabled: false },
      series: [
        {
          type: 'bar',
          name: t('pages.home.charts.count', 'Open findings'),
          color: seriesPalette(mode)[0],
          data: rows.map((r) => r.open),
        },
      ],
    };
  }, [data, mode, t]);

  const topRulesOptions: Options | null = useMemo(() => {
    if (!data || data.top_rules.length === 0) return null;
    const rows = data.top_rules;
    return {
      chart: { type: 'bar', height: Math.max(CHART_HEIGHT, rows.length * 44 + 80) },
      title: { text: undefined },
      xAxis: {
        // REV-1163: a chart label is plain text; drop a title's code-span backticks.
        categories: rows.map((r) => stripCodeSpans(r.name || r.rule_id || '—')),
        title: { text: null },
      },
      yAxis: {
        min: 0,
        allowDecimals: false,
        title: { text: t('pages.home.charts.count', 'Open findings') },
      },
      legend: { enabled: false },
      series: [
        {
          type: 'bar',
          name: t('pages.home.charts.count', 'Open findings'),
          color: seriesPalette(mode)[1],
          data: rows.map((r) => r.count),
        },
      ],
    };
  }, [data, mode, t]);

  const hasFindings = !!data && (data.totals?.open ?? 0) > 0;

  return (
    <Box>
      <Typography variant="h2" component="h1" gutterBottom>
        {t('pages.home.title', 'Dashboard')}
      </Typography>
      {/* REV-568: state the scope. "All" means all the organizations THIS user
          belongs to, and saying how many stops it reading as a claim about
          every organization that exists.

          The wrapper is ALWAYS rendered and only its child is conditional.
          Conditionally rendering the element itself changes the number of
          children in this subtree, which shifts every following sibling's
          position and makes React remount them — including the Highcharts
          cards, which then tear down and redraw. Caught because it broke the
          reflow test: the chart containers briefly did not exist. */}
      <Typography variant="body1" paragraph color="text.secondary">
        {t('pages.home.subtitle')}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress aria-label={t('pages.home.loading', 'Loading dashboard…')} />
        </Box>
      )}

      {!loading && !activeOrg && !error && (
        <Paper variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h5" component="h2" gutterBottom>
            {t('pages.home.noOrgSelected.title', 'Choose an organization')}
          </Typography>
          <Typography color="text.secondary" sx={{ mb: 2 }}>
            {t(
              'pages.home.noOrgSelected.body',
              'Pick an organization from the switcher in the header — or create one if you do not have any yet.'
            )}
          </Typography>
          {/* REV-676: this state covers TWO different users — one who has
              organizations but has not picked one, and one who has none at
              all. The old copy only addressed the first, and pointed the
              second at a switcher that does not render for them. A direct
              route out serves both and does not depend on the header. */}
          {/* REV-732: goes to a URL, so it is a link. */}
          <Button variant="contained" component={RouterLink} to="/orgs?create=1">
            {t('pages.home.noOrgSelected.create', 'Create an organization')}
          </Button>
        </Paper>
      )}

      {!loading && !error && data && (
        <>
          <Grid container spacing={3} sx={{ mb: 1 }}>
            <StatCard
              label={t('pages.home.stats.trackedPlaces', 'Tracked places')}
              value={data.totals?.contexts ?? 0}
              // Not a findings number — it is Context.count, so it drills into
              // the org's tracked places rather than a filtered findings list.
              to={`/orgs/${orgSlug}/contexts`}
            />
            <StatCard
              label={t('pages.home.stats.openFindings', 'Open findings')}
              value={data.totals?.open ?? 0}
              to={findingsHref(orgSlug)}
            />
            <StatCard
              label={t('pages.home.stats.critical', 'Critical')}
              value={data.by_severity?.critical ?? 0}
              color="error.main"
              to={findingsHref(orgSlug, { severity: 'critical' })}
            />
            <StatCard
              label={t('pages.home.stats.allFindings', 'All findings recorded')}
              value={data.totals?.all ?? 0}
              // This one counts EVERY status, so it must override the open-set
              // default or it would show fewer rows than the number clicked.
              to={findingsHref(orgSlug, { status: DASHBOARD_ALL_STATUSES })}
            />
          </Grid>

          {!hasFindings ? (
            <Paper variant="outlined" sx={{ p: 4, textAlign: 'center', mt: 2 }}>
              <Typography variant="h5" component="h2" gutterBottom>
                {t('pages.home.empty.title', 'No findings yet')}
              </Typography>
              <Typography color="text.secondary">
                {t(
                  'pages.home.empty.body',
                  'Run a test on a tracked place, or import scan history, to populate your dashboard.'
                )}
              </Typography>
            </Paper>
          ) : (
            <Grid container spacing={3} sx={{ mt: 0 }}>
              <ChartCard
                title={t('pages.home.charts.severity', 'Open findings by severity')}
                description={t('pages.home.charts.severityDesc')}
                options={severityOptions}
                tableLabel={t('pages.home.charts.showData', 'Show data table')}
                columns={[
                  t('pages.home.charts.table.severity', 'Severity'),
                  t('pages.home.charts.count', 'Open findings'),
                ]}
                rows={SEVERITY_ORDER.map((s) => ({
                  cells: [severityLabel(s), String(data.by_severity?.[s] ?? 0)],
                  to: findingsHref(orgSlug, { severity: s }),
                }))}
              />
              <ChartCard
                title={t('pages.home.charts.wcag', 'Open findings by WCAG level')}
                description={t('pages.home.charts.wcagDesc')}
                options={wcagOptions}
                tableLabel={t('pages.home.charts.showData', 'Show data table')}
                columns={[
                  t('pages.home.charts.table.wcagLevel', 'WCAG level'),
                  t('pages.home.charts.count', 'Open findings'),
                ]}
                rows={WCAG_ORDER.map((l) => ({
                  cells: [l === 'unspecified' ? '—' : l, String(data.by_wcag_level[l] ?? 0)],
                  to: findingsHref(orgSlug, { wcag_level: l }),
                }))}
              />
              {contentCategoryOptions && (
                <ChartCard
                  title={t('pages.home.charts.contentType', 'Open findings by content type')}
                  description={t('pages.home.charts.contentTypeDesc')}
                  options={contentCategoryOptions}
                  tableLabel={t('pages.home.charts.showData', 'Show data table')}
                  columns={[
                    t('pages.home.charts.table.contentType', 'Content type'),
                    t('pages.home.charts.count', 'Open findings'),
                  ]}
                  // No link: the findings list has no content-type filter yet,
                  // and a row that navigates to an unfiltered list is worse
                  // than a row that does not navigate at all.
                  rows={contentCategoryRows.map((r) => ({
                    cells: [r.category, String(r.count)],
                  }))}
                />
              )}
              {priorityOptions && (
                <ChartCard
                  title={t('pages.home.charts.priority', 'Open findings by priority')}
                  description={t('pages.home.charts.priorityDesc')}
                  options={priorityOptions}
                  tableLabel={t('pages.home.charts.showData', 'Show data table')}
                  columns={[
                    t('pages.home.charts.table.priority', 'Priority'),
                    t('pages.home.charts.count', 'Open findings'),
                  ]}
                  rows={priorityRows.map((r) => ({ cells: [r.label, String(r.count)] }))}
                />
              )}
              {byContextOptions && (
                <ChartCard
                  title={t('pages.home.charts.byContext', 'Open findings by tracked place')}
                  description={t('pages.home.charts.byContextDesc')}
                  options={byContextOptions}
                  tableLabel={t('pages.home.charts.showData', 'Show data table')}
                  columns={[
                    t('pages.home.charts.table.trackedPlace', 'Tracked place'),
                    t('pages.home.charts.count', 'Open findings'),
                  ]}
                  rows={data.by_context.map((r) => {
                    const to =
                      r.context_id && orgSlug
                        ? findingsHref(orgSlug, { context_id: r.context_id })
                        : undefined;
                    return {
                      cells: [contextLabel(r), String(r.open)],
                      ...(to ? { to } : {}),
                    };
                  })}
                />
              )}
              {topRulesOptions && (
                <ChartCard
                  title={t('pages.home.charts.topRules', 'Top rules')}
                  description={t('pages.home.charts.topRulesDesc')}
                  options={topRulesOptions}
                  tableLabel={t('pages.home.charts.showData', 'Show data table')}
                  columns={[
                    t('pages.home.charts.table.rule', 'Rule'),
                    t('pages.home.charts.count', 'Open findings'),
                  ]}
                  rows={data.top_rules.map((r) => ({
                    cells: [stripCodeSpans(r.name || r.rule_id || '—'), String(r.count)],
                    ...(r.rule_id ? { to: findingsHref(orgSlug, { rule_id: r.rule_id }) } : {}),
                  }))}
                />
              )}
            </Grid>
          )}
        </>
      )}
    </Box>
  );
}

/**
 * REV-481: the dashboard's numbers are clickable, and each link must land on
 * the population its number was computed from.
 *
 * organizationDashboard counts AUTO findings (FindingInstance) with
 * status IN (open, acknowledged, in_progress). The findings list defaults to
 * BOTH sources and "anything but resolved" — which also counts false_positive
 * and wont_fix. So a card reading "6" would link to a list showing more than 6
 * unless source and status are pinned here.
 *
 * `...extra` is spread last so a card with its own status (All findings
 * recorded, which counts every status) overrides the open-set default.
 */
const DASHBOARD_UNRESOLVED = 'open,acknowledged,in_progress';
const DASHBOARD_ALL_STATUSES = 'open,acknowledged,in_progress,resolved,false_positive,wont_fix';

/**
 * A drill-through into one organization's filtered findings.
 *
 * REV-568: returns undefined in the all-organizations view. The findings list
 * is org-scoped, so there is no honest destination for "critical across every
 * org" until REV-584 adds a cross-org findings page — and a link that silently
 * dropped the filter, or picked one org arbitrarily, would be worse than no
 * link. StatCard and the data tables already treat a missing href as "render
 * as plain text".
 */
function findingsHref(slug: string, extra: Record<string, string> = {}): string | undefined {
  // REV-589: a missing slug means no organization is chosen in the header;
  // callers render plain text rather than a link that goes nowhere.
  if (!slug) return undefined;
  return `/orgs/${slug}/findings?${new URLSearchParams({
    source: 'auto',
    status: DASHBOARD_UNRESOLVED,
    ...extra,
  }).toString()}`;
}

interface StatCardProps {
  label: string;
  value: number;
  color?: string;
  /** Where this number drills through to. Omitted = the card stays static. */
  to?: string;
}

function StatCard({ label, value, color, to }: StatCardProps) {
  const body = (
    <>
      <Typography variant="overline" color="text.secondary" component="h2">
        {label}
      </Typography>
      <Typography
        variant="h3"
        component="p"
        sx={{ color: color ?? 'text.primary', fontWeight: 700 }}
      >
        {formatNumber(value)}
      </Typography>
    </>
  );

  return (
    <Grid item xs={12} sm={6} md={3}>
      <Paper sx={{ p: 3, height: '100%' }}>
        {to ? (
          // The link wraps the label AND the value, so its accessible name is
          // computed from its own content ("Tracked places 12") — no aria-label
          // restating visible text, which is the two-labelling-strategies
          // problem REV-537 hit on the org switcher.
          <Link
            component={RouterLink}
            to={to}
            underline="hover"
            sx={{ display: 'block', color: 'inherit' }}
          >
            {body}
          </Link>
        ) : (
          body
        )}
      </Paper>
    </Grid>
  );
}

export default HomePage;
